import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../i18n';
import PublicLayout from '../components/PublicLayout';
import { CraftFoodLogo, LavashLogo, VeloraLogo } from '../components/brand/Logos';
import { LeafIcon, HeartIcon, MedalIcon, PeopleIcon } from '../components/brand/Icons';
import { ArrowRight, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function HomePage() {
  const { t } = useLanguage();

  const values = [
    { Icon: LeafIcon, label: t('valNatural'), key: 'natural' },
    { Icon: HeartIcon, label: t('valBetterChoice'), key: 'choice' },
    { Icon: MedalIcon, label: t('valQuality'), key: 'quality' },
    { Icon: PeopleIcon, label: t('valTrust'), key: 'trust' },
  ];

  return (
    <PublicLayout>
      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 bg-grain pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-32 lg:pt-32 lg:pb-44">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <p className="eyebrow mb-6" data-testid="hero-eyebrow">CRAFTFOOD</p>
              <h1 className="h-display text-5xl md:text-7xl lg:text-[5.5rem] mb-8 max-w-3xl" data-testid="hero-headline">
                {t('heroLine1')}<br/>
                <span className="text-[#C9A352]">{t('heroLine2')}</span><br/>
                {t('heroLine3')}
              </h1>
              <p className="text-lg md:text-xl text-[#1A1A1A]/65 max-w-xl leading-relaxed mb-10" data-testid="hero-sub">
                {t('heroSub')}
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/register" data-testid="hero-cta-primary">
                  <Button className="cff-button-primary h-12 px-7 text-base rounded-md">
                    {t('heroCtaPrimary')} <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/brands" data-testid="hero-cta-secondary">
                  <Button variant="outline" className="cff-button-outline h-12 px-7 text-base rounded-md">
                    {t('heroCtaSecondary')}
                  </Button>
                </Link>
              </div>
            </div>

            {/* Sub-brand teaser cards */}
            <div className="lg:col-span-5 grid grid-cols-1 gap-4">
              <div className="bg-[#F2EFE6] rounded-xl p-8 border border-[#A4B18E]/30 hover:border-[#556B2F] transition-colors group" data-testid="hero-brand-lavash">
                <LavashLogo size="md" className="mb-4" />
                <p className="text-sm text-[#556B2F]/70 leading-relaxed">
                  {t('lavashCardDesc')}
                </p>
                <Link to="/lavash" className="inline-flex items-center gap-2 mt-5 text-sm font-semibold text-[#556B2F] group-hover:gap-3 transition-all">
                  LAVASH <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
              <div className="bg-[#F6E9E5] rounded-xl p-8 border border-[#E8C5C8] hover:border-[#8E2A4E] transition-colors group" data-testid="hero-brand-velora">
                <div className="flex items-center justify-end mb-4">
                  <span className="inline-flex items-center gap-1 text-[10px] tracking-widest uppercase font-bold px-2 py-1 bg-[#8E2A4E] text-[#F6E9E5] rounded-sm">
                    <Sparkles className="h-3 w-3" /> {t('veloraSoon')}
                  </span>
                </div>
                <VeloraLogo size="md" className="mb-4" />
                <p className="text-sm text-[#6E4D3A]/80 leading-relaxed">
                  {t('veloraCardDesc')}
                </p>
                <Link to="/velora" className="inline-flex items-center gap-2 mt-5 text-sm font-semibold text-[#8E2A4E] group-hover:gap-3 transition-all italic font-playfair">
                  VELORA <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="bg-[#1A1A1A] text-[#F6F4EE] py-24 md:py-32" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mb-16">
            <p className="eyebrow text-[#C9A352] mb-6">{t('valuesTitle')}</p>
            <h2 className="text-4xl md:text-6xl font-extrabold tracking-tight text-[#F6F4EE]">
              {t('philosophyBody')}
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-12">
            {values.map(({ Icon, label, key }, i) => (
              <div key={key} data-testid={`value-${key}`} className="group">
                <div className="text-[#A4B18E] mb-4 inline-flex p-3 rounded-full border border-[#A4B18E]/40 group-hover:border-[#A4B18E] transition-colors">
                  <Icon width={28} height={28} />
                </div>
                <p className="text-[10px] tracking-[0.3em] text-[#C9A352] mb-2 font-semibold">0{i+1}</p>
                <p className="text-base font-semibold text-[#F6F4EE] leading-snug max-w-[180px]">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OUR BRANDS — simple presentation, no hierarchy */}
      <section className="py-24 md:py-32 bg-[#F6F4EE]" data-testid="brand-arch-section">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-16">
            <p className="eyebrow mb-6">{t('ourBrands')}</p>
            <h2 className="text-3xl md:text-5xl font-extrabold tracking-tight">
              {t('ourBrands')}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <Link
              to="/lavash"
              data-testid="brand-card-lavash"
              className="bg-[#F2EFE6] rounded-2xl p-10 md:p-12 text-left hover:shadow-lg transition-all cff-hover-lift border border-transparent hover:border-[#556B2F]/30"
            >
              <LavashLogo size="lg" className="mb-8" />
              <p className="text-base text-[#556B2F]/80 leading-relaxed">{t('lavashCardDesc')}</p>
              <span className="inline-flex items-center gap-2 mt-8 text-sm font-bold text-[#556B2F]">
                LAVASH <ArrowRight className="h-4 w-4" />
              </span>
            </Link>
            <Link
              to="/velora"
              data-testid="brand-card-velora"
              className="bg-[#F6E9E5] rounded-2xl p-10 md:p-12 text-left hover:shadow-lg transition-all cff-hover-lift border border-transparent hover:border-[#8E2A4E]/30 relative"
            >
              <span className="absolute top-6 right-6 inline-flex items-center gap-1 text-[10px] tracking-widest uppercase font-bold px-2 py-1 bg-[#8E2A4E] text-[#F6E9E5] rounded-sm">
                <Sparkles className="h-3 w-3" /> {t('veloraSoon')}
              </span>
              <VeloraLogo size="lg" className="mb-8" />
              <p className="text-base text-[#6E4D3A]/80 leading-relaxed">{t('veloraCardDesc')}</p>
              <span className="inline-flex items-center gap-2 mt-8 text-sm font-bold text-[#8E2A4E] italic font-playfair">
                VELORA <ArrowRight className="h-4 w-4" />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#556B2F] text-[#F2EFE6]" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <p className="eyebrow text-[#F2EFE6]/70 mb-6">{t('b2bNoticeTag')}</p>
          <h2 className="text-3xl md:text-5xl font-extrabold tracking-tight mb-6">
            {t('readyToOrder')}
          </h2>
          <p className="text-base md:text-lg text-[#F2EFE6]/80 mb-3 max-w-xl mx-auto">
            {t('b2bNoticeTitle')}
          </p>
          <p className="text-sm text-[#F2EFE6]/60 mb-10 max-w-xl mx-auto">
            {t('registerInMinute')}
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/register" data-testid="cta-register">
              <Button className="bg-[#F2EFE6] text-[#556B2F] hover:bg-[#F2EFE6]/90 h-12 px-8 text-base font-bold">
                {t('register')} <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/login" data-testid="cta-login">
              <Button variant="outline" className="bg-transparent border-[#F2EFE6] text-[#F2EFE6] hover:bg-[#F2EFE6]/10 h-12 px-8 text-base">
                {t('login')}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
